import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from '@material-ui/core';
import Loader from 'qs-components/Common/Loader';
import ProductLibraryView from 'qs-components/Catalogue/ProductLibraryUploader/ProductLibraryView';
import {
  addItemsToConfirmedOrderByBarcode,
  addItemsToOrderByBarcode,
  addProductsToOrderFromCatalogue,
  changeInquiryQuantity
} from 'qs-data-manager/Orders/OrdersProductsOperations';
import { reportError } from 'qs-helpers/ErrorReporting';
import { ReactComponent as CloseIcon } from 'qs-assets/Media/close.svg';
import { ReactComponent as CheckIcon } from 'qs-assets/Media/checkmark.svg';
import { ReactComponent as ScannerIcon } from 'qs-assets/Media/barcode.svg';
import { ReactComponent as InfoErrorIcon } from 'qs-assets/Media/info-error.svg';
import { getI18N } from 'qs-services/i18N';
import toastr from 'toastr';
import ScannedProduct from './ScannedProduct';
import './styles.scss';

const SCAN_DEBOUNCE_TIME = 300;
const ADDED_FEEDBACK_TIME = 2000;
const SHEET_TRANSITION_TIME = 300;

const Ripple = () => <div className="addItemsRipple" />;

const AnimatedEllipsis = ({ message }) => (
  <div className="addItemsLoadingDots">
    {message}
    <span className="addItemsDots" />
  </div>
);

export default ({ orderId, inquiries = [], isFinalized = false, onClose }) => {
  const { t } = getI18N();
  const inputRef = useRef(null);
  const debounceTimeout = useRef(null);
  const addedTimeout = useRef(null);
  const [barcode, setBarcode] = useState('');
  const [entered, setEntered] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [added, setAdded] = useState(false);
  const [updatingInquiryId, setUpdatingInquiryId] = useState(null);
  const [showCatalogue, setShowCatalogue] = useState(false);
  const [addingFromCatalogue, setAddingFromCatalogue] = useState(false);
  // Read while handling a blur, which the catalogue modal triggers before the state update lands
  const catalogueOpenRef = useRef(false);

  const focusInput = useCallback(() => {
    // The catalogue picker has inputs of its own, holding on to the focus would fight with them
    if (catalogueOpenRef.current || !inputRef.current) {
      return;
    }

    inputRef.current.focus();
  }, []);

  const handleBlur = useCallback(() => {
    setTimeout(focusInput, 0);
  }, [focusInput]);

  useEffect(() => {
    const frameId = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frameId);
  }, []);

  useEffect(
    () => () => {
      clearTimeout(debounceTimeout.current);
      clearTimeout(addedTimeout.current);
    },
    []
  );

  const handleClose = useCallback(() => {
    setEntered(false);
    setTimeout(onClose, SHEET_TRANSITION_TIME);
  }, [onClose]);

  const cleanUpBarcode = useCallback(() => {
    setBarcode('');
    focusInput();
  }, [focusInput]);

  const flashAdded = useCallback(() => {
    setAdded(true);
    clearTimeout(addedTimeout.current);
    addedTimeout.current = setTimeout(() => setAdded(false), ADDED_FEEDBACK_TIME);
  }, []);

  const handleAddError = useCallback(error => {
    if (!error || !error.isBackendMessage) {
      reportError(error);
      setFetchError(true);
      return;
    }

    setFetchError(error.message);
  }, []);

  const handleScan = useCallback(
    async scannedValue => {
      const scannedBarcode = (scannedValue || '').trim();
      if (!scannedBarcode) {
        return;
      }

      setFetching(true);
      setFetchError(null);
      try {
        if (isFinalized) {
          const { inventoryUpdated } = await addItemsToConfirmedOrderByBarcode({
            orderId,
            barcode: scannedBarcode
          });
          if (inventoryUpdated) {
            toastr.success(t('inventory_updated'));
          }
        } else {
          await addItemsToOrderByBarcode({ orderId, barcode: scannedBarcode });
        }
        flashAdded();
      } catch (error) {
        handleAddError(error);
      } finally {
        setFetching(false);
        cleanUpBarcode();
      }
    },
    [orderId, isFinalized, t, flashAdded, handleAddError, cleanUpBarcode]
  );

  const openCatalogue = useCallback(() => {
    catalogueOpenRef.current = true;
    setShowCatalogue(true);
  }, []);

  const closeCatalogue = useCallback(() => {
    catalogueOpenRef.current = false;
    setShowCatalogue(false);
    focusInput();
  }, [focusInput]);

  const handleCatalogueSelection = useCallback(
    async selectedProductIds => {
      closeCatalogue();
      if (!Array.isArray(selectedProductIds) || selectedProductIds.length === 0) {
        return;
      }

      setAddingFromCatalogue(true);
      setFetching(true);
      setFetchError(null);
      try {
        const { inventoryUpdated } = await addProductsToOrderFromCatalogue({
          orderId,
          productIds: selectedProductIds,
          isFinalized
        });
        if (inventoryUpdated) {
          toastr.success(t('inventory_updated'));
        }
        flashAdded();
      } catch (error) {
        handleAddError(error);
      } finally {
        setFetching(false);
        setAddingFromCatalogue(false);
        focusInput();
      }
    },
    [orderId, isFinalized, t, flashAdded, handleAddError, closeCatalogue, focusInput]
  );

  const handleInputChange = useCallback(
    value => {
      setBarcode(value);
      clearTimeout(debounceTimeout.current);
      debounceTimeout.current = setTimeout(() => handleScan(value), SCAN_DEBOUNCE_TIME);
    },
    [handleScan]
  );

  const handleQuantityChange = useCallback(
    async ({ inquiryId, itemCount }, delta) => {
      setUpdatingInquiryId(inquiryId);
      setFetchError(null);
      try {
        const { inventoryUpdated } = await changeInquiryQuantity({
          inquiryId,
          orderId,
          itemCount: itemCount + delta,
          isFinalized
        });
        if (inventoryUpdated) {
          toastr.success(t('inventory_updated'));
        }
      } catch (error) {
        setFetchError((error && error.message) || true);
      } finally {
        setUpdatingInquiryId(null);
        focusInput();
      }
    },
    [orderId, isFinalized, t, focusInput]
  );

  const handleKeyDown = useCallback(
    event => {
      if (event.key === 'Escape') {
        handleClose();
      }
    },
    [handleClose]
  );

  let status = 'scanning';
  if (fetching) {
    status = 'fetching';
  } else if (fetchError === true) {
    status = 'fetching-error';
  } else if (typeof fetchError === 'string') {
    status = 'already-exist';
  }

  const hasItems = inquiries.length > 0;
  const isBig = !hasItems && status === 'scanning';

  const renderStatus = () => {
    if (added) {
      return (
        <>
          <CheckIcon className="addItemsCheckIcon" />
          {t('item_added_successful')}
        </>
      );
    }

    if (status === 'fetching') {
      return (
        <>
          <Loader size="extraSmall" />
          {addingFromCatalogue ? t('adding_products') : t('fetching_product_details')}
        </>
      );
    }

    if (status === 'fetching-error') {
      return (
        <>
          <InfoErrorIcon className="addItemsErrorIcon" />
          {t('could_not_find_product_in_database')}
        </>
      );
    }

    if (status === 'already-exist') {
      return (
        <>
          <InfoErrorIcon className="addItemsErrorIcon" />
          {fetchError}
        </>
      );
    }

    if (!hasItems) {
      return (
        <>
          <Ripple />
          <span>{t('start_scanning')}</span>
        </>
      );
    }

    return (
      <>
        <ScannerIcon className="addItemsScannerIcon" />
        <AnimatedEllipsis message={t('continue_scanning')} />
      </>
    );
  };

  return (
    <>
      <div
        className={`addItemsOverlay${entered ? ' addItemsOverlayActive' : ''}`}
        onClick={handleClose}
      />
      <div
        className={`addItemsSheet${entered ? ' addItemsSheetActive' : ''}${
          isBig ? ' addItemsSheetTallStatus' : ''
        }`}
        onKeyDown={handleKeyDown}
      >
        <div className="addItemsSheetHeader">
          <div className="addItemsSheetHeaderTopRow">
            <div className="addItemsSheetTitle">{t('add_items')}</div>
            <CloseIcon className="addItemsCloseIcon" onClick={handleClose} />
          </div>
          <div className="addItemsSheetSubtitle">
            {t('add_products_by_scanning_or_from_catalogue')}
          </div>
          <div className="addItemsTabs">
            <button
              type="button"
              className={`addItemsTab${showCatalogue ? '' : ' addItemsTabActive'}`}
              onClick={closeCatalogue}
            >
              {t('scan_barcode')}
            </button>
            <button
              type="button"
              className={`addItemsTab${showCatalogue ? ' addItemsTabActive' : ''}`}
              onClick={openCatalogue}
            >
              {t('from_catalogue')}
            </button>
          </div>
        </div>

        <input
          className="addItemsGhostInput"
          autoFocus
          ref={inputRef}
          type="text"
          value={barcode}
          onBlur={handleBlur}
          onChange={event => handleInputChange(event.target.value)}
        />

        {hasItems && (
          <div className="addItemsProductList">
            {inquiries.map(inquiry => {
              const { inquiryId, itemCount = 1, finalPrice = 0, product = {} } = inquiry;
              return (
                <ScannedProduct
                  key={inquiryId}
                  name={product.name}
                  pictureUrl={product.pictureUrl}
                  price={finalPrice}
                  quantity={itemCount}
                  isLoading={updatingInquiryId === inquiryId}
                  onIncrement={() => handleQuantityChange(inquiry, 1)}
                  onDecrement={() => handleQuantityChange(inquiry, -1)}
                />
              );
            })}
          </div>
        )}

        <div
          className={`addItemsStatus addItemsStatusType-${status}${
            isBig ? ' addItemsStatusInitial' : ''
          }`}
        >
          {added && <div className="addItemsAnimatedSuccess" />}
          {['fetching-error', 'already-exist'].includes(status) && !added && (
            <div className="addItemsAnimatedError" />
          )}
          {renderStatus()}
        </div>
      </div>

      {showCatalogue && (
        <Modal open={true} onClose={closeCatalogue}>
          <div style={{ height: '100%', width: '100%' }}>
            <ProductLibraryView
              onCancel={closeCatalogue}
              excludeCurrentCatalogue={false}
              getSelectedProducts={handleCatalogueSelection}
            />
          </div>
        </Modal>
      )}
    </>
  );
};
